import React, { useMemo } from 'react';

// --- IMPORTACIÓN DE NUESTRAS UTILIDADES DE SEGUROS ---
import { parseCurrency, formatCurrencyUniversal } from '../../../../utils/segurosUtils';

// --- ICONOS SVG LOCALES PARA INTEGRIDAD AUTÓNOMA ---
const IconCash = () => (
    <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const IconAlert = () => (
    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
);

const IconTrashAlert = () => (
    <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const IconBan = () => (
    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
);

const IconCounter = () => (
    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
    </svg>
);

/**
 * KpisRefacturaciones - Dashboard analítico de cauciones refacturadas y estados de crédito.
 * @param {Array} registros - Listado de registros filtrados de tipo "Refacturacion".
 * @param {Boolean} isDbLoaded - Flag de carga de datos de Firestore.
 */
const KpisRefacturaciones = ({ registros, isDbLoaded }) => {

    const metrics = useMemo(() => {
        if (!isDbLoaded || !registros || registros.length === 0) {
            return {
                cobradoARS: 0, cobradoUSD: 0,
                adeudadoARS: 0, adeudadoUSD: 0,
                incobrableARS: 0, incobrableUSD: 0,
                caucionesAnuladasCant: 0,
                cantDeudaReclamada: 0,
                cantMoroso: 0
            };
        }

        let cobradoARS = 0; let cobradoUSD = 0;
        let adeudadoARS = 0; let adeudadoUSD = 0;
        let incobrableARS = 0; let incobrableUSD = 0;
        let caucionesAnuladasCant = 0;
        let cantDeudaReclamada = 0;
        let cantMoroso = 0;

        registros.forEach(item => {
            // Saneamos el importe financiero (Premio/Prima de Refacturación)
            const importe = parseCurrency(item.premioRefacturacion || item.primaEmitidaRefacturacion || item.prima);
            const monedaRaw = String(item.monedaRefacturacion || item.moneda || '$').toUpperCase().trim();
            const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

            // Normalización de estados para evaluación limpia
            const estadoRefac = String(item.estadoRefacturacion || '').trim().toLowerCase();
            const estadoPolizaRaiz = String(item.estadoEmision || '').trim().toLowerCase();

            // 4 & 5. Evaluación de Anulaciones (Prioridad: Si el trámite o la póliza raíz están anulados)
            const esAnulado = estadoRefac === 'anulada' || estadoPolizaRaiz === 'anulada' || estadoPolizaRaiz === 'cancelada';
            if (esAnulado) {
                caucionesAnuladasCant += 1;
                return;
            }

            // 1. Importe Total Cobrado
            const esCobrado = ['abonado - falta recibo', 'abonado', 'debitada'].includes(estadoRefac);

            // 2. Importe Adeudado
            const esAdeudado = ['deuda reclamada', 'refacturación enviada', 'moroso', 'revisión'].includes(estadoRefac);

            // 3. Deuda Incobrable
            const esIncobrable = estadoRefac === 'incobrable';

            // --- ACUMULACIÓN DE MONTOS MULTIMONEDA ---
            if (esCobrado) {
                if (esUSD) cobradoUSD += importe;
                else cobradoARS += importe;
            } else if (esAdeudado) {
                if (esUSD) adeudadoUSD += importe;
                else adeudadoARS += importe;
            } else if (esIncobrable) {
                if (esUSD) incobrableUSD += importe;
                else incobrableARS += importe;
            }

            // --- CONTADORES DE VOLUMEN OPERATIVO ---
            // 6. Cantidad en Deuda Reclamada
            if (estadoRefac === 'deuda reclamada') {
                cantDeudaReclamada += 1;
            }
            // 7. Cantidad en Moroso
            if (estadoRefac === 'moroso') {
                cantMoroso += 1;
            }
        });

        return {
            cobradoARS, cobradoUSD,
            adeudadoARS, adeudadoUSD,
            incobrableARS, incobrableUSD,
            caucionesAnuladasCant,
            cantDeudaReclamada,
            cantMoroso
        };
    }, [registros, isDbLoaded]);

    if (!isDbLoaded) {
        return (
            <div className="p-8 text-center text-slate-400 italic text-sm select-none">
                Cargando análisis de refacturaciones...
            </div>
        );
    }

    if (registros.length === 0) {
        return (
            <div className="p-12 text-center text-slate-400 italic text-sm bg-white rounded-3xl border border-slate-200 select-none">
                No se encontraron trámites de refacturación cargados en este rango para procesar KPIs.
            </div>
        );
    }

    return (
        <div className="space-y-8 text-left animate-fade-in">
            
            {/* --- SECCIÓN I: METRICAS FINANCIERAS MULTIMONEDA --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                
                {/* 1. TOTAL COBRADO */}
                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Total Cobrado (Refac)</p>
                        <h4 className="text-xl font-black text-emerald-600 mt-2 font-mono tracking-tight">
                            {formatCurrencyUniversal(metrics.cobradoARS, '$')}
                        </h4>
                        {metrics.cobradoUSD > 0 && (
                            <h5 className="text-xs font-black text-blue-600 mt-1 font-mono">
                                {formatCurrencyUniversal(metrics.cobradoUSD, 'U$S')}
                            </h5>
                        )}
                        <p className="text-[9px] text-slate-400 mt-1.5 font-bold uppercase tracking-wider">Ingresos percibidos</p>
                    </div>
                    <span className="p-3 bg-emerald-50 text-emerald-600 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center font-mono font-black text-xs select-none">
                        <IconCash />
                    </span>
                </div>

                {/* 2. TOTAL ADEUDADO */}
                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Saldo Adeudado (Mora)</p>
                        <h4 className="text-xl font-black text-red-600 mt-2 font-mono tracking-tight">
                            {formatCurrencyUniversal(metrics.adeudadoARS, '$')}
                        </h4>
                        {metrics.adeudadoUSD > 0 && (
                            <h5 className="text-xs font-black text-blue-600 mt-1 font-mono">
                                {formatCurrencyUniversal(metrics.adeudadoUSD, 'U$S')}
                            </h5>
                        )}
                        <p className="text-[9px] text-slate-400 mt-1.5 font-bold uppercase tracking-wider">Saldos activos de cauciones</p>
                    </div>
                    <span className="p-3 bg-red-50 text-red-600 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center font-mono font-black text-xs select-none">
                        <IconAlert />
                    </span>
                </div>

                {/* 3. TOTAL INCOBRABLE */}
                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Saldo Incobrable</p>
                        <h4 className="text-xl font-black text-slate-500 mt-2 font-mono tracking-tight">
                            {formatCurrencyUniversal(metrics.incobrableARS, '$')}
                        </h4>
                        {metrics.incobrableUSD > 0 && (
                            <h5 className="text-xs font-black text-blue-600 mt-1 font-mono">
                                {formatCurrencyUniversal(metrics.incobrableUSD, 'U$S')}
                            </h5>
                        )}
                        <p className="text-[9px] text-slate-400 mt-1.5 font-bold uppercase tracking-wider">Cauciones dadas de baja</p>
                    </div>
                    <span className="p-3 bg-slate-50 border border-slate-100 text-slate-500 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center font-mono font-black text-xs select-none">
                        <IconTrashAlert />
                    </span>
                </div>

                {/* 5. CAUCIONES ANULADAS */}
                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Cauciones Anuladas</p>
                        <h4 className="text-xl font-black text-amber-600 mt-2 font-mono">
                            {metrics.caucionesAnuladasCant}
                        </h4>
                        <p className="text-[9px] text-slate-400 mt-1.5 font-bold uppercase tracking-wider">Contratos cancelados</p>
                    </div>
                    <span className="p-3 bg-amber-50 text-amber-600 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center font-mono font-black text-xs select-none">
                        <IconBan />
                    </span>
                </div>

            </div>

            {/* --- SECCIÓN II: DETALLE Y VOLUMEN OPERATIVO --- */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-5">
                <div>
                    <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">Alertas de Carteras Críticas</h4>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Volumen total de refacturaciones con alto riesgo crediticio</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-1">
                    
                    {/* CARD CONTADOR: DEUDA RECLAMADA */}
                    <div className="bg-slate-50/50 border border-slate-150 rounded-2xl p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                            <span className="p-2.5 bg-blue-50 text-blue-700 rounded-xl shrink-0"><IconCounter /></span>
                            <div className="text-left">
                                <span className="text-[10px] font-black text-slate-400 uppercase block tracking-wider leading-none">En Deuda Reclamada</span>
                                <span className="text-[11px] text-slate-500 font-bold mt-1 block">Gestiones prejudiciales activas</span>
                            </div>
                        </div>
                        <span className="text-xl font-black font-mono text-blue-900 bg-white border border-slate-150 px-4 py-1.5 rounded-xl shadow-sm select-none">
                            {metrics.cantDeudaReclamada}
                        </span>
                    </div>

                    {/* CARD CONTADOR: MOROSO */}
                    <div className="bg-slate-50/50 border border-slate-150 rounded-2xl p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                            <span className="p-2.5 bg-rose-50 text-rose-700 rounded-xl shrink-0"><IconCounter /></span>
                            <div className="text-left">
                                <span className="text-[10px] font-black text-slate-400 uppercase block tracking-wider leading-none">En Estado Moroso</span>
                                <span className="text-[11px] text-slate-500 font-bold mt-1 block">Incumplimientos de cuota extendidos</span>
                            </div>
                        </div>
                        <span className="text-xl font-black font-mono text-rose-700 bg-white border border-slate-150 px-4 py-1.5 rounded-xl shadow-sm select-none">
                            {metrics.cantMoroso}
                        </span>
                    </div>

                </div>
            </div>

            {/* HOJA DE ESTILOS INLINE DE APOYO */}
            <style>{`
                .animate-fade-in { animation: fadeIn 0.25s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default KpisRefacturaciones;